import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/App';
import OrgChart from '@/components/OrgChart';
import { KPICard, WidgetCard, PrimaryButton, Pill } from '@/components/ui/native';
import { UserPlus } from 'lucide-react';

function TeamManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [activeTab, setActiveTab] = useState('members');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCounts, setDeleteCounts] = useState({ leads: 0, tasks: 0, direct_reports: 0 });
  const [deleteCountsLoading, setDeleteCountsLoading] = useState(false);
  const [reassignToId, setReassignToId] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const { user: currentUser } = useAuth();

  // Form states
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'sdr',
    send_welcome_email: true
  });
  const [passwordForm, setPasswordForm] = useState({
    new_password: '',
    confirm_password: '',
    notify_user: true,
    mode: 'set'
  });
  const [newRole, setNewRole] = useState('');

  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
  });

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const password = createForm.password;
    setPasswordValidation({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  }, [createForm.password]);

  const isPasswordStrong = Object.values(passwordValidation).every(Boolean);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Admin endpoint returns ALL users (for the admin tab); for non-admin
      // roles we use /users which returns the caller + their subordinates.
      const role = currentUser?.role;
      const useAdminEndpoint = role === 'admin' || role === 'company_sales_head';
      const url = useAdminEndpoint ? `${API}/admin/users` : `${API}/users`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong) {
      showMessage('error', 'Password does not meet requirements');
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users`, createForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', `User ${createForm.email} created successfully.`);
      setShowCreateModal(false);
      setCreateForm({ name: '', email: '', password: '', role: 'sdr', send_welcome_email: true });
      fetchUsers();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to create user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/users/${selectedUser.id}/role`, 
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showMessage('success', `Role updated to ${formatRole(newRole)}`);
      setShowEditRoleModal(false);
      fetchUsers();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to update role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      showMessage('error', 'Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      showMessage('error', 'Password must be at least 8 characters');
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/${selectedUser.id}/set-password`,
        { password: passwordForm.new_password, notify_user: passwordForm.notify_user },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showMessage('success', 'Password updated successfully');
      setShowPasswordModal(false);
      setPasswordForm({ new_password: '', confirm_password: '', notify_user: true, mode: 'set' });
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to set password');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Deactivate this user? They will not be able to log in.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/${userId}/deactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', 'User deactivated');
      fetchUsers();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/${userId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', 'User activated');
      fetchUsers();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to activate user');
    }
  };

  const handleDeleteUser = async (member) => {
    setSelectedUser(member);
    setReassignToId('');
    setDeleteConfirmText('');
    setDeleteCounts({ leads: 0, tasks: 0, direct_reports: 0 });
    setShowDeleteModal(true);
    setDeleteCountsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/users/${member.id}/data-counts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteCounts(r.data || { leads: 0, tasks: 0, direct_reports: 0 });
    } catch (error) {
      // non-fatal; modal still works, just with zeroed counts
      console.error('data-counts failed', error);
    } finally {
      setDeleteCountsLoading(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;
    const hasData = deleteCounts.leads > 0 || deleteCounts.tasks > 0 || deleteCounts.direct_reports > 0;
    if (hasData && !reassignToId) {
      showMessage('error', 'Please select a user to reassign data to.');
      return;
    }
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
      showMessage('error', 'Type DELETE to confirm.');
      return;
    }
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = reassignToId ? { reassign_to: reassignToId } : {};
      const r = await axios.delete(`${API}/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      const m = r.data || {};
      showMessage(
        'success',
        `Deleted "${selectedUser.email}" — ${m.reassigned_reports || 0} reports, ${m.reassigned_leads || 0} leads, ${m.reassigned_tasks || 0} tasks reassigned.`
      );
      setShowDeleteModal(false);
      setSelectedUser(null);
      setReassignToId('');
      setDeleteConfirmText('');
      fetchUsers();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const formatRole = (role) => {
    const roleLabels = {
      admin: 'Admin',
      company_sales_head: 'Company Head',
      country_sales_head: 'Country Head',
      regional_sales_head: 'Regional Head',
      manager: 'Manager',
      team_leader: 'Team Leader',
      sales_executive: 'Sales Exec',
      sdr: 'SDR',
      solutions_architect: 'Solutions Architect',
      pre_sales_engineer: 'Pre-Sales Engineer',
    };
    return roleLabels[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-700 border-red-200',
      company_sales_head: 'bg-orange-100 text-orange-700 border-orange-200',
      country_sales_head: 'bg-amber-100 text-amber-700 border-amber-200',
      regional_sales_head: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      manager: 'bg-blue-100 text-blue-700 border-blue-200',
      team_leader: 'bg-purple-100 text-purple-700 border-purple-200',
      sales_executive: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      sdr: 'bg-green-100 text-green-700 border-green-200',
      solutions_architect: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      pre_sales_engineer: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    };
    return colors[role] || 'bg-slate-100 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700';
  };

  // Initials from full name — first + last initial, fallback to first 2 chars of email
  const getInitials = (member) => {
    const name = (member?.name || '').trim();
    if (name) {
      const parts = name.split(/\s+/);
      const first = parts[0]?.[0] || '';
      const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
      return (first + last).toUpperCase() || name[0].toUpperCase();
    }
    const email = (member?.email || '').trim();
    return email ? email.slice(0, 2).toUpperCase() : '??';
  };

  // Subtle role-tinted avatar background — clean B2B aesthetic, not toy emojis
  const getAvatarTone = (role) => {
    const tones = {
      admin:               'bg-slate-900 text-white dark:bg-white dark:text-slate-900',
      company_sales_head:  'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
      country_sales_head:  'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
      regional_sales_head: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
      manager:             'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      team_leader:         'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
      sales_executive:     'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
      sdr:                 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
      solutions_architect: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
      pre_sales_engineer:  'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
    };
    return tones[role] || 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
  };

  const groupedByRole = users.reduce((acc, user) => {
    if (!acc[user.role]) acc[user.role] = [];
    acc[user.role].push(user);
    return acc;
  }, {});

  const isAdmin = currentUser?.role === 'admin';

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchQuery || 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const roles = ['admin', 'company_sales_head', 'country_sales_head', 'regional_sales_head', 'manager', 'team_leader', 'sales_executive', 'sdr', 'solutions_architect', 'pre_sales_engineer'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="team-loading">
        <div className="animate-spin w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Team Management</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {isAdmin ? 'Manage users, roles, and permissions' : 'View your team members'}
          </p>
        </div>
        {isAdmin && (
          <PrimaryButton
            onClick={() => setShowCreateModal(true)}
            data-testid="create-user-btn"
            icon={<UserPlus className="w-4 h-4" />}
          >
            Add User
          </PrimaryButton>
        )}
      </div>

      {/* Message */}
      {message.text && (
        <div className={`px-4 py-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-700 p-1 rounded-lg w-fit" data-testid="team-tabs">
        <button
          onClick={() => setActiveTab('members')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'members' ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
          data-testid="tab-members"
        >
          Team Members
        </button>
        <button
          onClick={() => setActiveTab('org-chart')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'org-chart' ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
          data-testid="tab-org-chart"
        >
          Org Chart
        </button>
      </div>

      {activeTab === 'org-chart' ? (
        <OrgChart />
      ) : (
      <>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard title="Total Users" value={users.length} testId="kpi-total-users" />
        <KPICard title="Active" value={users.filter(u => u.is_active !== false).length} testId="kpi-active-users" />
        <KPICard title="Admins" value={groupedByRole.admin?.length || 0} testId="kpi-admins" />
        <KPICard title="SDRs" value={groupedByRole.sdr?.length || 0} testId="kpi-sdrs" />
      </div>

      {/* Search & Filter */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          >
            <option value="all">All Roles</option>
            {roles.map(role => (
              <option key={role} value={role}>{formatRole(role)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Team Members Grid */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Team Members ({filteredUsers.length})</span>
        </div>
        
        {filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-12 h-12 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-slate-500 dark:text-slate-400">No team members found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredUsers.map((member) => (
              <div key={member.id || member.email} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition" data-testid={`user-row-${member.email}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${getAvatarTone(member.role)}`}
                      title={formatRole(member.role)}
                      data-testid={`user-avatar-${member.email}`}
                    >
                      {getInitials(member)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900 dark:text-white truncate">{member.name}</p>
                        {member.email === currentUser?.email && (
                          <span className="px-2 py-0.5 text-[10px] bg-blue-100 text-blue-700 rounded-full font-medium">You</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{member.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getRoleBadgeColor(member.role)}`}>
                      {formatRole(member.role)}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      member.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500 dark:text-slate-400'
                    }`}>
                      {member.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                    
                    {isAdmin && member.email !== currentUser?.email && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setSelectedUser(member); setNewRole(member.role); setShowEditRoleModal(true); }}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Change Role"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => { setSelectedUser(member); setShowPasswordModal(true); }}
                          className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition"
                          title="Reset Password"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                        </button>
                        {member.is_active !== false ? (
                          <button
                            onClick={() => handleDeactivateUser(member.id)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Deactivate"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivateUser(member.id)}
                            className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                            title="Activate"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteUser(member)}
                          className="p-2 text-slate-400 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition"
                          title="Delete user"
                          data-testid={`delete-user-${member.id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      </>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-800">
              <h2 className="text-lg font-semibold">Add New User</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 dark:text-slate-400">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleCreateUser} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Full Name</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  placeholder="John Doe"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  placeholder="john@company.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Password</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                  required
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  placeholder="••••••••"
                />
                <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                  <span className={passwordValidation.minLength ? 'text-green-600' : 'text-slate-400'}>✓ 8+ characters</span>
                  <span className={passwordValidation.hasUppercase ? 'text-green-600' : 'text-slate-400'}>✓ Uppercase</span>
                  <span className={passwordValidation.hasLowercase ? 'text-green-600' : 'text-slate-400'}>✓ Lowercase</span>
                  <span className={passwordValidation.hasNumber ? 'text-green-600' : 'text-slate-400'}>✓ Number</span>
                  <span className={passwordValidation.hasSpecial ? 'text-green-600' : 'text-slate-400'}>✓ Special char</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Role</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                >
                  {roles.map(role => (
                    <option key={role} value={role}>{formatRole(role)}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="send_welcome"
                  checked={createForm.send_welcome_email}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, send_welcome_email: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="send_welcome" className="text-sm text-slate-600 dark:text-slate-400">Send welcome email</label>
              </div>
              
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 dark:text-slate-100">
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={actionLoading || !isPasswordStrong}
                  className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 disabled:opacity-50"
                >
                  {actionLoading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold">Change Role</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{selectedUser.email}</p>
            </div>
            
            <div className="p-4 space-y-4">
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
              >
                {roles.map(role => (
                  <option key={role} value={role}>{formatRole(role)}</option>
                ))}
              </select>
              
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowEditRoleModal(false)} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 dark:text-slate-100">
                  Cancel
                </button>
                <button 
                  onClick={handleUpdateRole}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 disabled:opacity-50"
                >
                  {actionLoading ? 'Saving...' : 'Update Role'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold">Reset Password</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{selectedUser.email}</p>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">New Password</label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  placeholder="••••••••"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  placeholder="••••••••"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="notify_user"
                  checked={passwordForm.notify_user}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, notify_user: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="notify_user" className="text-sm text-slate-600 dark:text-slate-400">Notify user via email</label>
              </div>
              
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowPasswordModal(false)} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 dark:text-slate-100">
                  Cancel
                </button>
                <button 
                  onClick={handleSetPassword}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 disabled:opacity-50"
                >
                  {actionLoading ? 'Saving...' : 'Reset Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Delete User Modal — Hard delete + reassignment */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delete-user-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </span>
                  Delete user
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {selectedUser.name} <span className="text-slate-400">({selectedUser.email})</span>
                </p>
              </div>
              <button
                onClick={() => { setShowDeleteModal(false); setSelectedUser(null); }}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                data-testid="delete-modal-close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Pre-flight summary */}
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-3">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                  This user currently owns
                </p>
                {deleteCountsLoading ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div data-testid="delete-counts-leads">
                      <div className="text-2xl font-bold text-slate-900 dark:text-white">{deleteCounts.leads}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">Leads</div>
                    </div>
                    <div data-testid="delete-counts-tasks">
                      <div className="text-2xl font-bold text-slate-900 dark:text-white">{deleteCounts.tasks}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">Tasks</div>
                    </div>
                    <div data-testid="delete-counts-reports">
                      <div className="text-2xl font-bold text-slate-900 dark:text-white">{deleteCounts.direct_reports}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">Direct reports</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Reassignment dropdown */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Reassign data to
                  {(deleteCounts.leads > 0 || deleteCounts.tasks > 0 || deleteCounts.direct_reports > 0) && (
                    <span className="text-red-600 ml-0.5">*</span>
                  )}
                </label>
                <select
                  value={reassignToId}
                  onChange={(e) => setReassignToId(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  data-testid="reassign-to-select"
                >
                  <option value="">— Select a user —</option>
                  {users
                    .filter(u => u.id !== selectedUser.id && u.is_active !== false)
                    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email))
                    .map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.email}) — {formatRole(u.role)}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  All leads, tasks, and direct reports will be transferred to this user.
                </p>
              </div>

              {/* Warning */}
              <div className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
                <p className="font-semibold mb-1">This permanently deletes the user.</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>Account row removed from the database</li>
                  <li>Active sessions revoked immediately</li>
                  <li>Microsoft 365 tokens cleared</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>

              {/* Type-to-confirm */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  placeholder="DELETE"
                  data-testid="delete-confirm-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => { setShowDeleteModal(false); setSelectedUser(null); }}
                  className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                  data-testid="delete-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteUser}
                  disabled={
                    actionLoading ||
                    deleteCountsLoading ||
                    deleteConfirmText.trim().toUpperCase() !== 'DELETE' ||
                    ((deleteCounts.leads > 0 || deleteCounts.tasks > 0 || deleteCounts.direct_reports > 0) && !reassignToId)
                  }
                  className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="delete-modal-confirm"
                >
                  {actionLoading ? 'Deleting…' : 'Delete user'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamManagement;
